import React, { useState, useRef, useEffect } from "react";
import { Box } from "@mui/material";
import { motion, useDragControls } from "framer-motion";

// Sleep-/swipe-laag rond één misc-bestand. Eigen dragControls per instantie,
// zodat een swipe tijdens de slide-animatie niet ook het verdwijnende bestand
// meesleept. De sleep start handmatig: als een kind (de PdfCanvasViewer, bij
// pannen met spatie of middelste muisknop) preventDefault() aanroept, blijft
// de swipe uit en pant de pdf. Slepen vanaf tekst in een pdf selecteert die
// tekst, dus ook dan geen swipe.
//
// touch (alleen CaseDetailTouch): geen framer-drag maar een eigen veeg-
// herkenning bij loslaten. Framer pakte de eerste vinger al op, waardoor een
// pinch (tweede vinger) het bestand meesleepte of zelfs wisselde. Een veeg
// telt alleen met één vinger en zonder pinch (Safari-gesture) ertussen.
export function MiscSwipeArea({ enabled, onSwipe, style, children, touch = false }) {
  const dragControls = useDragControls();
  const swipeRef = useRef(null);
  const areaRef = useRef(null);
  useEffect(() => {
    if (!touch) return undefined;
    const el = areaRef.current; if (!el) return undefined;
    const onGesture = () => { if (swipeRef.current) swipeRef.current.cancelled = true; };
    el.addEventListener("gesturestart", onGesture);
    return () => el.removeEventListener("gesturestart", onGesture);
  }, [touch]);
  if (touch) {
    const end = (e) => {
      const sw = swipeRef.current;
      if (!sw || sw.id !== e.pointerId) return;
      swipeRef.current = null;
      if (sw.cancelled || e.type === "pointercancel" || !enabled) return;
      const dx = e.clientX - sw.x, dy = e.clientY - sw.y;
      const dt = Math.max(performance.now() - sw.t, 1);
      if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy) * 1.5) return;
      onSwipe?.(e, { offset: { x: dx, y: dy }, velocity: { x: (dx / dt) * 1000, y: (dy / dt) * 1000 } });
    };
    return (
      <div
        ref={areaRef}
        style={style}
        onPointerDown={(e) => {
          if (e.pointerType !== "touch") return;
          // Tweede vinger, of een ingezoomde afbeelding die zelf pant: geen veeg
          if (swipeRef.current || e.defaultPrevented) { if (swipeRef.current) swipeRef.current.cancelled = true; return; }
          if (e.target.closest?.("[data-pdf-native-scroll]")) return;
          swipeRef.current = { id: e.pointerId, x: e.clientX, y: e.clientY, t: performance.now(), cancelled: false };
        }}
        onPointerUp={end}
        onPointerCancel={end}
      >
        {children}
      </div>
    );
  }
  return (
    <motion.div
      drag={enabled ? "x" : false}
      dragControls={dragControls}
      dragListener={false}
      dragConstraints={{ left: 0, right: 0 }}
      dragElastic={1}
      onDragEnd={onSwipe}
      onPointerDown={(e) => {
        if (!enabled || e.defaultPrevented || (e.pointerType === "mouse" && e.button !== 0)) return;
        if (e.target.closest?.("[data-pdf-text-layer] span")) return;
        // Touch op een pdf die zelf (native) scrolt: dat is scrollen, geen swipe
        if (e.pointerType === "touch" && e.target.closest?.("[data-pdf-native-scroll]")) return;
        dragControls.start(e);
      }}
      style={style}
    >
      {children}
    </motion.div>
  );
}

// Afbeelding in Attachments met Ctrl/Cmd+scrollwiel (en trackpad-pinch, die
// als ctrl+wheel binnenkomt) inzoomen rond de cursor. Niet kleiner dan passend;
// zoomen gaat via een transform, de layout van de img blijft gelijk.
// Touch: dubbeltik = inzoomen op die plek (of terug naar passend), en
// ingezoomd met één vinger pannen of met twee vingers verder pinchen. Pinchen
// vanaf passend kan niet: dan is de eerste vinger al een swipe naar het
// volgende bestand (MiscSwipeArea).
const IMAGE_MAX_ZOOM = 8;
const DOUBLE_TAP_ZOOM = 2.5;
const DOUBLE_TAP_MS = 300;
// touch (alleen CaseDetailTouch): ook pinchen via Safari's gesture-events,
// ook vanaf passend. iOS breekt bij een pinch de vingers af (pointercancel),
// dus de pointer-pinch hieronder kwam daar niet op gang.
export function ZoomableImage({ sx, touch = false, ...props }) {
  const imgRef = useRef(null);
  const [zoom, setZoom] = useState({ s: 1, x: 0, y: 0 });
  // Alleen tijdens de dubbelklik-reset een transitie; bij het wielzoomen niet
  // (dat zou achter de cursor aan gaan lopen)
  const [resetting, setResetting] = useState(false);
  const zoomRef = useRef(zoom);
  zoomRef.current = zoom;

  // Zoom naar ns met het punt (clientX, clientY) op dezelfde plek
  const zoomAt = (clientX, clientY, ns) => {
    const el = imgRef.current;
    if (!el) return null;
    const { s, x, y } = zoomRef.current;
    const rect = el.getBoundingClientRect();
    const w = rect.width / s, h = rect.height / s;
    // punt in onge-transformeerde img-coördinaten (transform-origin 0 0)
    const px = clientX - (rect.left - x), py = clientY - (rect.top - y);
    const clamp = (v, min) => Math.min(Math.max(v, min), 0);
    return {
      s: ns,
      x: clamp(px - (px - x) * (ns / s), w * (1 - ns)),
      y: clamp(py - (py - y) * (ns / s), h * (1 - ns)),
    };
  };

  useEffect(() => {
    const el = imgRef.current;
    if (!el) return;
    const onWheel = (e) => {
      if (!e.ctrlKey && !e.metaKey) return;
      e.preventDefault();
      setResetting(false);
      const { s } = zoomRef.current;
      // deltaY begrensd: een muiswiel-tik (~100) zou anders bijna 3x per stap zoomen
      const dy = Math.min(Math.max(e.deltaY, -40), 40);
      const ns = Math.min(Math.max(s * Math.exp(-dy * 0.01), 1), IMAGE_MAX_ZOOM);
      const next = zoomAt(e.clientX, e.clientY, ns);
      if (next) setZoom(next);
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, []);

  // Ingezoomd: slepen met links pant de afbeelding. preventDefault() houdt
  // de swipe naar het volgende bestand tegen (zie MiscSwipeArea).
  const panRef = useRef(null);
  const [panning, setPanning] = useState(false);
  const touchesRef = useRef(new Map()); // pointerId -> { x, y }
  const pinchRef = useRef(null);
  const tapRef = useRef(null); // { t, x, y } van de vorige tik

  const gestureRef = useRef(null);
  useEffect(() => {
    if (!touch) return undefined;
    const el = imgRef.current;
    if (!el || !("ongesturestart" in window)) return undefined;
    const onStart = (e) => {
      e.preventDefault();
      panRef.current = null;
      pinchRef.current = null;
      setResetting(false);
      gestureRef.current = { s0: zoomRef.current.s };
    };
    const onChange = (e) => {
      const g = gestureRef.current; if (!g) return;
      e.preventDefault();
      const ns = Math.min(Math.max(g.s0 * e.scale, 1), IMAGE_MAX_ZOOM);
      const next = zoomAt(e.clientX, e.clientY, ns);
      if (next) { zoomRef.current = next; setZoom(next); }
    };
    const onEnd = (e) => {
      if (!gestureRef.current) return;
      e.preventDefault();
      gestureRef.current = null;
      tapRef.current = null;
      // Bijna passend: netjes terug naar passend
      if (zoomRef.current.s < 1.05) { setResetting(true); setZoom({ s: 1, x: 0, y: 0 }); }
    };
    el.addEventListener("gesturestart", onStart, { passive: false });
    el.addEventListener("gesturechange", onChange, { passive: false });
    el.addEventListener("gestureend", onEnd, { passive: false });
    return () => {
      el.removeEventListener("gesturestart", onStart);
      el.removeEventListener("gesturechange", onChange);
      el.removeEventListener("gestureend", onEnd);
    };
    // zoomAt leest alleen refs
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [touch]);

  const startPan = (clientX, clientY) => {
    const { s, x, y } = zoomRef.current;
    const rect = imgRef.current.getBoundingClientRect();
    panRef.current = { startX: clientX, startY: clientY, x, y, w: rect.width / s, h: rect.height / s };
  };

  const onPointerDown = (e) => {
    if (e.pointerType === "touch") {
      touchesRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY, t: performance.now(), sx: e.clientX, sy: e.clientY });
      if (zoomRef.current.s === 1) return; // swipe/dubbeltik
      e.preventDefault();
      setResetting(false);
      try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* ignore */ }
      const pts = [...touchesRef.current.values()];
      if (pts.length >= 2) {
        panRef.current = null;
        const [a, b] = pts;
        pinchRef.current = { d0: Math.hypot(a.x - b.x, a.y - b.y) || 1, s0: zoomRef.current.s };
      } else {
        startPan(e.clientX, e.clientY);
        setPanning(true);
      }
      return;
    }
    const { s } = zoomRef.current;
    if (s === 1 || (e.pointerType === "mouse" && e.button !== 0)) return;
    e.preventDefault();
    setResetting(false);
    startPan(e.clientX, e.clientY);
    e.currentTarget.setPointerCapture(e.pointerId);
    setPanning(true);
  };
  const onPointerMove = (e) => {
    if (e.pointerType === "touch" && touchesRef.current.has(e.pointerId)) {
      const pt = touchesRef.current.get(e.pointerId);
      pt.x = e.clientX; pt.y = e.clientY;
      if (gestureRef.current) return; // pinch loopt via de gesture-events
      const pinch = pinchRef.current;
      if (pinch) {
        const [a, b] = [...touchesRef.current.values()];
        if (!b) return;
        const ns = Math.min(Math.max(pinch.s0 * (Math.hypot(a.x - b.x, a.y - b.y) / pinch.d0), 1), IMAGE_MAX_ZOOM);
        const next = zoomAt((a.x + b.x) / 2, (a.y + b.y) / 2, ns);
        if (next) setZoom(next);
        return;
      }
    }
    const p = panRef.current;
    if (!p) return;
    const { s } = zoomRef.current;
    const clamp = (v, min) => Math.min(Math.max(v, min), 0);
    setZoom({
      s,
      x: clamp(p.x + e.clientX - p.startX, p.w * (1 - s)),
      y: clamp(p.y + e.clientY - p.startY, p.h * (1 - s)),
    });
  };
  const onPointerUp = (e) => {
    if (e?.pointerType === "touch") {
      const pt = touchesRef.current.get(e.pointerId);
      if (!pt) return;
      touchesRef.current.delete(e.pointerId);
      // Dubbeltik: inzoomen op die plek, of (ingezoomd) terug naar passend
      const now = performance.now();
      const isTap = now - pt.t < 250 && Math.hypot(e.clientX - pt.sx, e.clientY - pt.sy) < 10 && !pinchRef.current;
      if (isTap) {
        const prev = tapRef.current;
        if (prev && now - prev.t < DOUBLE_TAP_MS && Math.hypot(e.clientX - prev.x, e.clientY - prev.y) < 30) {
          tapRef.current = null;
          setResetting(true);
          if (zoomRef.current.s > 1) setZoom({ s: 1, x: 0, y: 0 });
          else {
            const next = zoomAt(e.clientX, e.clientY, DOUBLE_TAP_ZOOM);
            if (next) setZoom(next);
          }
        } else {
          tapRef.current = { t: now, x: e.clientX, y: e.clientY };
        }
      }
      if (pinchRef.current && touchesRef.current.size < 2) {
        pinchRef.current = null;
        // Eén vinger blijft staan: daarmee verder pannen
        const rest = [...touchesRef.current.values()][0];
        if (rest && zoomRef.current.s > 1) startPan(rest.x, rest.y);
        else { panRef.current = null; setPanning(false); }
        return;
      }
      if (touchesRef.current.size > 0) return;
    }
    if (!panRef.current) return;
    panRef.current = null;
    setPanning(false);
  };

  return (
    <Box
      component="img"
      ref={imgRef}
      {...props}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      // Dubbelklik: terug naar passend
      onDoubleClick={() => {
        if (zoomRef.current.s === 1) return;
        setResetting(true);
        setZoom({ s: 1, x: 0, y: 0 });
      }}
      sx={{
        ...sx,
        transition: [sx?.transition, resetting && "transform 250ms ease"].filter(Boolean).join(", "),
        transformOrigin: "0 0",
        transform: `translate(${zoom.x}px, ${zoom.y}px) scale(${zoom.s})`,
        ...(zoom.s > 1 && { cursor: panning ? "grabbing" : "grab", touchAction: "none" }),
      }}
    />
  );
}
